import ApiInstance from './instance'
import {
  URL_MAPPING,
  IFacilityFiltersDTO,
  IFacilityResponseDTO,
  IFacility,
  IPointClickCareCredentials,
  IPayerType,
  IUpdateFacilityFromPccDTO,
  IFacilityCollectionSettings,
  IFacilityPaymentAccount,
  IFacilityAutoApplyDTO,
  ISetupWizardFacilityStatementSettings,
  IFacilityAdvancedSettings,
  IClaimFlow,
  IClaim,
  IFacilityClaimFlowsResult,
  IClaimFlowDTO,
  IFacilityClaimFlowsFilters,
  IClaimTask,
  IFacilityPayerGroup,
  IGetUploadFacilityDataTaskFilters,
  ICreditCardOptionsToggleDTO,
  IFacilityCredentials,
  GetFacilityCredentialsFilters
} from '@zunta/configurations'

const { facility: facilityUrls } = URL_MAPPING

export default class FacilityApi {
  instance: ApiInstance

  constructor (instance: ApiInstance) {
    this.instance = instance
  }

  setupPayerTypes (payerTypes: IPayerType[]): Promise<'OK'> {
    return this.instance.doPost(`${facilityUrls.prefix}${facilityUrls.setupPayerTypes.path}`, { payerTypes })
  }

  getFacilityPaymentAccount (facilityId: number | string): Promise<IFacilityPaymentAccount> {
    return this.instance.doGet(`${facilityUrls.prefix}${facilityUrls.getPaymentAccount.path}/${facilityId}`)
  }

  getFacilityPaymentAccounts (facilityIds: (number | string)[]): Promise<IFacilityPaymentAccount[]> {
    return this.instance.doPost(`${facilityUrls.prefix}/getPaymentAccounts/`, { facilityIds })
  }

  saveFacilityPaymentAccount (mode: string, data: Partial<IFacilityPaymentAccount>, voidedCheckFile: File | null, withdrawnVoidedCheckFile: File | null): Promise<IFacilityPaymentAccount> {
    const formData = new FormData()
    Object.keys(data).forEach((fieldName: string) => {
      const value = (data as any)[fieldName]
      if (value !== undefined && value !== null) {
        formData.append(fieldName, value)
      }
    })
    if (voidedCheckFile) {
      formData.append('voidedCheckFile', voidedCheckFile)
    }
    if (withdrawnVoidedCheckFile) {
      formData.append('withdrawnVoidedCheckFile', withdrawnVoidedCheckFile)
    }
    return this.instance.doPost(`${facilityUrls.prefix}${facilityUrls.savePaymentAccount.path}/${mode}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  }

  getFacilityAutoApplySettings (facilityId: number | string): Promise<IFacilityAutoApplyDTO | null> {
    return this.instance.doGet(`${facilityUrls.prefix}${facilityUrls.getAutoApplySettings.path}/${facilityId}`)
  }

  saveFacilityAutoApplySettings (settings: IFacilityAutoApplyDTO): Promise<IFacilityAutoApplyDTO> {
    return this.instance.doPost(`${facilityUrls.prefix}${facilityUrls.saveAutoApplySettings.path}`, settings)
  }

  getFacilityList (filters: IFacilityFiltersDTO): Promise<IFacilityResponseDTO> {
    return this.instance.doGet(`${facilityUrls.prefix}${facilityUrls.list.path}`, filters, null, { isCompressed: true })
  }

  deleteFacility (facilityId: number | string): Promise<'OK'> {
    return this.instance.doDelete(`${facilityUrls.prefix}${facilityUrls.deleteFacility.path}/${facilityId}`)
  }

  getFacilitiesActivePayerTypesMap (facilities: number[]): Promise<{ [k: string]: IPayerType[] }> {
    return this.instance.doPost(`${facilityUrls.prefix}${facilityUrls.getFacilitiesActivePayerTypesMap.path}`, { facilities }, {}, { isCompressed: true })
  }

  getFacilityPayers (facilityId: number | string): Promise<IPayerType[]> {
    return this.instance.doGet(`${facilityUrls.prefix}${facilityUrls.getFacilityPayerTypes.path}/${facilityId}`, {}, 'getFacilityPayerTypes')
  }

  updatePayerTypes (facilityId: number | string, payerTypes: IPayerType[]): Promise<IPayerType[]> {
    return this.instance.doPost(`${facilityUrls.prefix}${facilityUrls.updatePayerTypes.path}/${facilityId}`, { payerTypes })
  }

  changePayerTypeStatus (payerTypeId: number, status: boolean): Promise<'OK'> {
    return this.instance.doPut(`${facilityUrls.prefix}${facilityUrls.changePayerTypeStatus.path}/${payerTypeId}`, { status })
  }

  getFacilitiesByPointClickCareCredentials = async (c: IPointClickCareCredentials): Promise<{ name: string, originId: string }[]> => {
    return this.instance.doPost(`${facilityUrls.prefix}${facilityUrls.getFacilities.path}`, c)
  }

  async getOTPCodeForUsername (username: string) {
    return this.instance.doGet(`pcc/mfaCode`, { username })
  }

  async updateFacilityCredentials (facilityCredentialsDto: Partial<IFacilityCredentials>) {
    return this.instance.doPatch(`facilities/updateFacilityCredentials`, facilityCredentialsDto)
  }

  fetchFacilitiesWithDetails = async (f: IPointClickCareCredentials): Promise<'OK'> => {
    return this.instance.doPost(`${facilityUrls.prefix}${facilityUrls.fetchFacilityDetails.path}`, f)
  }

  fetchFacilityUpdates (facilityId: number | string, options: IUpdateFacilityFromPccDTO): Promise<'OK'> {
    return this.instance.doPost(`${facilityUrls.prefix}${facilityUrls.fetchFacilityUpdatesFromPcc.path}/${facilityId}`, { options })
  }

  createFacility (facilityData: IFacility): Promise<IFacility> {
    return this.instance.doPost(`${facilityUrls.prefix}/createFacility`, facilityData)
  }

  toggleManualPCCForChain (useManualPCCImport: boolean): Promise<'OK'> {
    return this.instance.doPut(`${facilityUrls.prefix}/toggleManualPCCForChain`, { useManualPCCImport })
  }

  getFacility (id: number | string): Promise<{ facility: IFacility }> {
    return this.instance.doGet(`${facilityUrls.prefix}${facilityUrls.getFacility.path}/${id}`, {}, 'getFacilityById')
  }

  updateFacility (id: number, updateDTO: Partial<IFacility>) {
    return this.instance.doPut(`${facilityUrls.prefix}${facilityUrls.updateFacility.path}/${id}`, updateDTO)
  }

  uploadLogo (facilityId: number | string, logoFile: any) {
    const form = new FormData()
    form.append('logo', logoFile)
    return this.instance.doPost(`${facilityUrls.prefix}${facilityUrls.changeLogo.path}/${facilityId}`, form, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  }

  updateFacilityCollectionSettings (facilityId: number | string, data: Partial<IFacilityCollectionSettings>): Promise<IFacilityCollectionSettings> {
    return this.instance.doPut(`${facilityUrls.prefix}${facilityUrls.updateFacilityCollectionSettings.path}/${facilityId}`, data)
  }

  getFacilityCollectionSettings (facilityId: number | string): Promise<IFacilityCollectionSettings | null> {
    return this.instance.doGet(`${facilityUrls.prefix}${facilityUrls.getFacilityCollectionSettings.path}/${facilityId}`)
  }

  getAllPayerTypesMap () {
    return this.instance.doGet(`${facilityUrls.prefix}${facilityUrls.getAllPayerTypesMap.path}`)
  }

  setupFacilityStatementSettings (statementSettings: ISetupWizardFacilityStatementSettings): Promise<'OK'> {
    return this.instance.doPost(`${facilityUrls.prefix}${facilityUrls.setupFacilityStatementSettings.path}`, { statementSettings })
  }

  uploadSelectedFacilitiesData (username: string, password: string, facilitiesData: any[]): Promise<'OK'> {
    const formData = new FormData()
    formData.append('originIds', facilitiesData.map((facilityData: any) => facilityData.originId).join(','))
    formData.append('username', username)
    formData.append('password', password)
    facilitiesData.forEach((facilityData: any) => {
      formData.append(`${facilityData.originId}-name`, facilityData.facilityName)
      if (facilityData.logo) {
        formData.append(`${facilityData.originId}-logo`, facilityData.logo)
      }
      if (facilityData.website) {
        formData.append(`${facilityData.originId}-website`, facilityData.website)
      }
      if (facilityData.phoneNumber) {
        formData.append(`${facilityData.originId}-phoneNumber`, facilityData.phoneNumber)
      }
      if (facilityData.email) {
        formData.append(`${facilityData.originId}-email`, facilityData.email)
      }
    })
    return this.instance.doPost(`${facilityUrls.prefix}${facilityUrls.importSelectedFacilitiesData.path}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  }

  setPaymentAccountEnableEditing (facilityId: number | string, enableEditing: boolean): Promise<IFacilityPaymentAccount> {
    return this.instance.doPut(`${facilityUrls.prefix}/paymentAccountEnableEditing/${facilityId}`, { enableEditing })
  }

  fetchFacilityAdvancedSettings (facilityId: number): Promise<IFacilityAdvancedSettings[]> {
    return this.instance.doGet(`${facilityUrls.prefix}/advancedSettings/${facilityId}`)
  }

  saveFacilityAdvancedSettings (facilityId: number, settings: any): Promise<IFacilityAdvancedSettings[]> {
    return this.instance.doPut(`${facilityUrls.prefix}/advancedSettings/${facilityId}`, { settings })
  }

  deleteFacilityAdvancedSetting (facilityId: number, settingId: number): Promise<void[]> {
    return this.instance.doDelete(`${facilityUrls.prefix}/advancedSettings/${facilityId}/${settingId}`)
  }

  getAllFacilitiesPayerTypes (filters: any): Promise<{ payerTypes: IPayerType[], total: number }> {
    return this.instance.doGet(`${facilityUrls.prefix}/getFacilitiesPayersTypes`, filters)
  }

  getWillowPayers (filters: any): Promise<any> {
    return this.instance.doGet(`${facilityUrls.prefix}/getWillowPayers`, filters)
  }

  getClaimFlow (flowId: number): Promise<IClaimFlowDTO> {
    return this.instance.doGet(`/claim-flow/${flowId}`)
  }

  getClaimsByFilters (filters: any): Promise<{ claims: IClaim[], total: number }> {
    return this.instance.doGet(`/claims/all`, filters)
  }

  getFacilityClaimFlows (facilityId: number | string, filters: IFacilityClaimFlowsFilters): Promise<{ total: number, items: IFacilityClaimFlowsResult[] }> {
    return this.instance.doGet(`/facility-claim-flows/${facilityId}`, filters)
  }

  getClaimTasksByFIlter (filters: any): Promise<{ claimTasks: IClaimTask[], total: number }> {
    return this.instance.doGet('/claim/tasks-all', filters)
  }

  getClaimTaskById (id: number | string): Promise<IClaimTask> {
    return this.instance.doGet(`/claim/tasks/${id}`)
  }

  updateClaimTask (id: number | string, data: Partial<IClaimTask>): Promise<IClaimTask> {
    return this.instance.doPatch(`/claim/tasks/${id}`, data)
  }

  createClaimFlow (data: any): Promise<IClaimFlow> {
    return this.instance.doPost(`/claim-flow/${data.facilityId}`, data.data)
  }

  updateClaimFlow (data: any): Promise<'OK'> {
    return this.instance.doPut(`/claim-flow/${data.claimFlowId}`, data.data)
  }

  deleteClaimFlow (flowId: number): Promise<'OK'> {
    return this.instance.doDelete(`/claim-flow/${flowId}`)
  }

  getFacilityPayerGroups (facilityId: number) {
    return this.instance.doGet(`/facilities/getFacilityPayerGroups/${facilityId}`)
  }

  createFacilityPayerGroup (group: Partial<IFacilityPayerGroup>) {
    return this.instance.doPost('/facilities/createFacilityPayerGroup', group)
  }

  updateFacilityPayerGroup (facilityPayerGroupId: number, group: Partial<IFacilityPayerGroup>) {
    return this.instance.doPut(`/facilities/updateFacilityPayerGroup/${facilityPayerGroupId}`, group)
  }

  deleteFacilityPayerGroup (facilityPayerGroupId: number) {
    return this.instance.doDelete(`/facilities/deleteFacilityPayerGroup/${facilityPayerGroupId}`)
  }

  uploadResidentContacts (facilityId: number, file: any) {
    const form = new FormData()
    form.append('file', file)
    return this.instance.doPost(`/residents/uploadCustomRecipientList/${facilityId}`, form, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  }

  patchFacilityAdvancedSettings (settingId: number, data: Partial<IFacilityAdvancedSettings>) {
    return this.instance.doPut(`facilities/patchAdvancedFacilitySettings/${settingId}`, data)
  }

  uploadReports (facilityId: number, files: any) {
    return this.instance.doPost(`/pcc/uploadFacilityData/${facilityId}`, files, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  }

  getPccUpdateRequestsList (filters: IGetUploadFacilityDataTaskFilters) {
    return this.instance.doGet('/pcc/pccUploadTasks', filters)
  }

  getPccFacilityCredentials (filters: GetFacilityCredentialsFilters): Promise<{ list: IFacilityCredentials[], total: number }> {
    return this.instance.doGet('/pcc/facilityCredentials/list', filters, null, { isCompressed: true })
  }

  assignUserToTaskGroup (usernameOrTaskId: { username?: string, taskId?: number }) {
    if (usernameOrTaskId.username) {
      return this.instance.doPut(`/pcc/assignUserToTaskGroup`, { username: usernameOrTaskId.username })
    } else if (usernameOrTaskId.taskId) {
      return this.instance.doPut(`/pcc/assignUserToTaskGroup`, { taskId: usernameOrTaskId.taskId })
    }
  }

  takePccRequestTask (taskId: number) {
    return this.instance.doPut(`/pcc/takePccRequestTask/${taskId}`)
  }

  downloadPccUploadFile (fileIds: number[], fileName: string) {
    return this.instance.downloadFilesFromUrl({
      pccUpload: {
        urlStr: `pcc/downloadPccUploadFile`,
        ids: fileIds,
        fileName
      }
    })
  }

  ccOptionsToggle (creditCardDTO: ICreditCardOptionsToggleDTO) {
    return this.instance.doFetch<number>(
      'facilities/cc-options',
      creditCardDTO,
      'PUT',
      true
    ) as Promise<Response>
  }

  downloadHippaAgreement (userName: string | null, facilities: string) {
    this.instance.doGetDownload(
      `${facilityUrls.prefix}${facilityUrls.hippaAgreementTemplate.path}`,
      { userName, facilities },
      'HIPAA Business Associate Agreement.pdf'
    )
  }

  downloadLicenseAgreement (userName: string | null, facilities: string) {
    this.instance.doGetDownload(
      `${facilityUrls.prefix}${facilityUrls.zuntaLicenseTemplate.path}`,
      { userName, facilities },
      'Zunta End User License Agreement.pdf'
    )
  }
}
